import {
  fileExtensionForMime,
  hasCanvasCaptureStream,
  hasMediaRecorder,
  isIOS,
  pickSupportedMimeType,
} from '../utils/browserSupport';
import { CompositeCanvasRecorder } from './CompositeCanvasRecorder';
import type { PerformanceProfile } from '../performance/PerformanceProfile';

/**
 * - 'video'      : enregistrement vidéo composite (cas nominal Android Chrome, Safari iOS ≥ 14.5)
 * - 'screenshot' : pas de MediaRecorder/captureStream → capture photo composite
 * - 'none'       : même la capture est impossible → message enregistrement d'écran natif
 */
export type RecordingCapability = 'video' | 'screenshot' | 'none';

export interface RecordingResult {
  blob: Blob;
  mimeType: string;
  fileName: string;
}

export class RecordingService {
  private recorder: CompositeCanvasRecorder | null = null;
  private mimeType: string | null = null;

  detectCapability(): RecordingCapability {
    if (hasMediaRecorder() && hasCanvasCaptureStream() && pickSupportedMimeType() !== null) {
      return 'video';
    }
    if (typeof document !== 'undefined' && !!document.createElement('canvas').getContext('2d')) {
      return 'screenshot';
    }
    return 'none';
  }

  /** Message honnête sur la limitation, selon la plateforme. */
  fallbackMessage(): string {
    if (isIOS()) {
      return "L'enregistrement vidéo n'est pas disponible sur cette version d'iOS. "
        + "Vous pouvez capturer une photo de l'expérience, ou utiliser l'enregistrement "
        + "d'écran natif d'iOS (Centre de contrôle → Enregistrement de l'écran).";
    }
    return "L'enregistrement vidéo n'est pas pris en charge par ce navigateur. "
      + "Vous pouvez capturer une photo, ou utiliser l'enregistreur d'écran de votre téléphone.";
  }

  startVideo(
    video: HTMLVideoElement,
    glCanvas: HTMLCanvasElement,
    profile: PerformanceProfile,
  ): void {
    const mimeType = pickSupportedMimeType();
    if (!mimeType) throw new Error('Aucun format vidéo supporté');
    this.mimeType = mimeType;
    this.recorder = new CompositeCanvasRecorder(video, glCanvas, {
      maxWidth: profile.recordingMaxWidth,
      fps: profile.recordingFps,
      mimeType,
    });
    this.recorder.start();
  }

  async stopVideo(): Promise<RecordingResult> {
    if (!this.recorder || !this.mimeType) throw new Error('Aucun enregistrement en cours');
    const blob = await this.recorder.stop();
    const ext = fileExtensionForMime(this.mimeType);
    const result: RecordingResult = {
      blob,
      mimeType: blob.type || this.mimeType,
      fileName: `daemon-ar-${Date.now()}.${ext}`,
    };
    this.recorder = null;
    return result;
  }

  async captureScreenshot(
    video: HTMLVideoElement,
    glCanvas: HTMLCanvasElement,
    profile: PerformanceProfile,
  ): Promise<RecordingResult> {
    const shot = new CompositeCanvasRecorder(video, glCanvas, {
      maxWidth: profile.recordingMaxWidth,
      fps: 1,
      mimeType: 'video/webm', // non utilisé pour la capture
    });
    const blob = await shot.captureFrame();
    return { blob, mimeType: 'image/png', fileName: `daemon-ar-${Date.now()}.png` };
  }
}
